import { useCallback, useEffect, useRef, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { getCycleCommerceService, createCycleService } from "modules/monitoreo/services/crearCiclo";
import { bogotaNow, entryToISO, normalizePlate } from "../model/cycleModel";

export const useFormCrearCiclo = (onSubmit, initialData) => {
  const [commerces, setCommerces] = useState([]);
  const [loadingCommerces, setLoadingCommerces] = useState(false);
  const [errorCommerces, setErrorCommerces] = useState(null);
  const [submitError, setSubmitError] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [backendCreateResult, setBackendCreateResult] = useState(null);
  const mounted = useRef(false);
  const pendingLoad = useRef(null);
  const submitting = useRef(false);
  const initial = useRef({ ...bogotaNow(), commerce: "", plate: "", ...initialData });

  const reloadCommerces = useCallback(async () => {
    if (pendingLoad.current) return pendingLoad.current;
    setLoadingCommerces(true);
    setErrorCommerces(null);
    pendingLoad.current = (async () => {
      try {
        const list = await getCycleCommerceService();
        if (mounted.current)
          setCommerces(
            list
              .filter((item) => item?.idcommerce != null && item.namecomercio?.trim())
              .map((item) => ({
                id: String(item.idcommerce),
                code: String(item.idcommerce),
                name: item.namecomercio.trim(),
              }))
              .sort((a, b) => a.name.localeCompare(b.name))
          );
      } catch (error) {
        if (mounted.current) setErrorCommerces(error.message);
      } finally {
        pendingLoad.current = null;
        if (mounted.current) setLoadingCommerces(false);
      }
    })();
    return pendingLoad.current;
  }, []);

  useEffect(() => {
    mounted.current = true;
    reloadCommerces();
    return () => {
      mounted.current = false;
    };
  }, [reloadCommerces]);

  const formik = useFormik({
    initialValues: initial.current,
    validationSchema: Yup.object({
      commerce: Yup.string().required("Seleccione un comercio"),
      plate: Yup.string()
        .transform(normalizePlate)
        .matches(/^[A-Z]{3}\d{3}$/, "Use una placa como ABC123")
        .required("Ingrese la placa"),
      date: Yup.string().required("Seleccione la fecha"),
      time: Yup.string().required("Seleccione la hora"),
    }).test("entry", "Fecha u hora inválida", function (values) {
      try {
        const entry = entryToISO(values.date, values.time);
        return (
          new Date(entry).getTime() <= Date.now() ||
          this.createError({ path: "time", message: "El ingreso no puede ser futuro" })
        );
      } catch {
        return this.createError({ path: "date", message: "Fecha u hora inválida" });
      }
    }),
    onSubmit: async (values, helpers) => {
      if (submitting.current) return { success: false };
      submitting.current = true;
      setSubmitError(null);
      setSubmitSuccess(false);
      setBackendCreateResult(null);
      let result;
      try {
        const commerce = commerces.find((item) => item.id === values.commerce);
        if (!commerce) throw new Error("Seleccione un comercio válido");
        result = await createCycleService({
          commerce: commerce.name,
          plate: normalizePlate(values.plate),
          entry: entryToISO(values.date, values.time),
        });
        if (!mounted.current) return result;
        setBackendCreateResult(result);
        if (!result.success)
          throw new Error(result.error || result.message || "No se pudo confirmar la creación");
        setSubmitSuccess(true);
        helpers.resetForm({ values: { ...bogotaNow(), commerce: "", plate: "" } });
      } catch (error) {
        result = { success: false, error: error.message };
        if (mounted.current) setSubmitError(error.message);
      } finally {
        submitting.current = false;
        if (mounted.current) helpers.setSubmitting(false);
      }
      // Un fallo al refrescar la vista no modifica el resultado de la operación.
      if (mounted.current && onSubmit) {
        try {
          await onSubmit({ ...values, ...result, backendResult: result });
        } catch {
          /* El resultado permanece disponible. */
        }
      }
      return result;
    },
  });
  const executeSubmit = async () => {
    if (submitting.current) return { success: false };
    return formik.submitForm();
  };
  return {
    ...formik,
    commerces,
    loadingCommerces,
    errorCommerces,
    reloadCommerces,
    submitError,
    setSubmitError,
    submitSuccess,
    setSubmitSuccess,
    backendCreateResult,
    loading: formik.isSubmitting,
    executeSubmit,
  };
};
